import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from "../ui";
import { Archive, ArchiveRestore, ArrowLeft, BarChart3, Calendar, Copy, Edit, Link, Trash2, Users } from "lucide-react";
import { toast } from "react-toastify";
import { fetchSurveyById, updateSurvey, deleteSurvey, fetchSurveyResults } from "../../api/survey.api";
import logger from "../../utils/logger";
import SurveyDetailSkeleton from "./SurveyDetailSkeleton";
import storage from "../../utils/storage";
import { ConfirmModal } from "../common";

const statusConfig = {
  Actif:    { label: "Actif",     className: "bg-success/10 text-success border-success/20" },
  draft:    { label: "Brouillon", className: "bg-muted text-muted-foreground border-muted" },
  Clôturé:  { label: "Clôturé",  className: "bg-accent/10 text-accent border-accent/20" },
};

function SurveyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [survey, setSurvey] = useState(null);
  const [results, setResults] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [archived, setArchived] = useState(false);
  const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: null });
  const [actionLoading, setActionLoading] = useState(false);
  const role = storage.getRole();

  useEffect(() => {
    const loadSurvey = async () => {
      try {
        setLoading(true);
        const surveyData = await fetchSurveyById(id);
        setSurvey(surveyData);
        setArchived(surveyData.archived || false);

        try {
          const resultsData = await fetchSurveyResults(id);
          setResults(resultsData);
        } catch (err) {
          logger.warn('Impossible de charger les résultats:', err);
        }
      } catch (err) {
        logger.error('Erreur lors du chargement du sondage:', err);
        setError('Impossible de charger le sondage');
        toast.error('Erreur lors du chargement du sondage');
      } finally {
        setLoading(false);
      }
    };

    if (id) loadSurvey();
  }, [id]);

  const handleArchive = () => setConfirmModal({ isOpen: true, type: 'archive' });
  const handleDelete  = () => setConfirmModal({ isOpen: true, type: 'delete' });

  const handleConfirmAction = async () => {
    const { type } = confirmModal;
    setActionLoading(true);
    try {
      if (type === 'archive') {
        await updateSurvey(id, { archived: !archived });
        setArchived(!archived);
        toast.success(archived ? 'Sondage désarchivé !' : 'Sondage archivé !');
      } else if (type === 'delete') {
        await deleteSurvey(id);
        toast.success('Sondage supprimé !');
        navigate('/gerant/gestion-sondages');
      }
      setConfirmModal({ isOpen: false, type: null });
    } catch (err) {
      logger.error('Erreur action:', err);
      toast.error(err.response?.data?.error || err.response?.data?.detail || 'Erreur lors de l\'action');
    } finally {
      setActionLoading(false);
    }
  };
  console.log(role)

  // Navigation vers les résultats 
  const handleViewResults = () => {
    const path = role === 'ADMIN'
      ? `/admin/gestion-sondage/${id}/resultats`
      : `/gerant/gestion-sondages/${id}/resultats`;
    navigate(path);
  };

  if (loading) return <SurveyDetailSkeleton />;

  if (error || !survey) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
          <ArrowLeft className="h-4 w-4" />Retour
        </Button>
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <p className="text-red-700">{error || 'Sondage non trouvé'}</p>
        </div>
      </div>
    );
  }

  const status           = statusConfig[survey.computed_status] || statusConfig.Clôturé;
  const respondedCount   = results?.data?.stats?.responded        || 0;
  const totalParticipants = results?.data?.stats?.total_participants || 0;
  const progress         = results?.data?.stats?.participation_rate || 0;

  return (
    <div className="space-y-6">
      <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
        <ArrowLeft className="h-4 w-4" />Retour
      </Button>

      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <h1 className="font-display text-2xl font-bold text-foreground">{survey.title}</h1>
            <Badge variant="outline" className={status.className}>{status.label}</Badge>
            <Badge variant="outline" className="text-xs">
              {survey.category === "EVENT"        ? "Événementiel"
               : survey.category === "INTERNAL"  ? "Interne"
               : survey.category === "EXTERNAL"  ? "Externe"
               : survey.category}
            </Badge>
            {archived          && <Badge variant="outline" className="bg-muted text-xs">Archivé</Badge>}
            {survey.is_anonymous && <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">Anonyme</Badge>}
          </div>
          <p className="text-sm text-muted-foreground">{survey.description}</p>
        </div>

        {role === 'Gerant' && (
          <div className="flex gap-2 shrink-0 flex-wrap">
            <Button variant="outline" size="sm" className="gap-1" onClick={() => navigate(`/gerant/gestion-sondages/modifier/${id}`)}>
              <Edit className="h-3.5 w-3.5" />Modifier
            </Button>
            <Button variant="outline" size="sm" className="gap-1" onClick={handleArchive}>
              {archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
              {archived ? "Désarchiver" : "Archiver"}
            </Button>
            <Button variant="outline" size="sm" className="gap-1 text-destructive" onClick={handleDelete}>
              <Trash2 className="h-3.5 w-3.5" />Supprimer
            </Button>
          </div>
        )}
      </div>

      {/* Quick access cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">

        {/*  Voir les résultats — CORRIGÉ */}
        <Card
          className="shadow-card border-0 cursor-pointer hover:shadow-lg transition-shadow group"
          onClick={handleViewResults}
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
              <BarChart3 className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <p className="font-display font-semibold text-foreground">Voir les résultats</p>
              <p className="text-xs text-muted-foreground">
                {respondedCount} réponse{respondedCount > 1 ? 's' : ''} · Graphiques et export CSV
              </p>
            </div>
          </CardContent>
        </Card>

        <Card
          className="shadow-card border-0 cursor-pointer hover:shadow-lg transition-shadow group"
          onClick={() => navigate(`/gerant/gestion-sondages/${id}/participants`)}
        >
          <CardContent className="p-5 flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
              <Users className="h-6 w-6 text-accent" />
            </div>
            <div className="flex-1">
              <p className="font-display font-semibold text-foreground">Gérer les participants</p>
              <p className="text-xs text-muted-foreground">
                {respondedCount}/{results?.data?.participants?.length || 0} ont répondu · Inviter et relancer
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card className="shadow-card border-0">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-display font-bold text-foreground">{respondedCount}</p>
            <p className="text-xs text-muted-foreground">Réponses</p>
          </CardContent>
        </Card>
        <Card className="shadow-card border-0">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-display font-bold text-primary">{progress}%</p>
            <p className="text-xs text-muted-foreground">Complétion</p>
          </CardContent>
        </Card>
        <Card className="shadow-card border-0">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-display font-bold text-foreground">{totalParticipants}</p>
            <p className="text-xs text-muted-foreground">Participants</p>
          </CardContent>
        </Card>
        <Card className="shadow-card border-0">
          <CardContent className="p-4 text-center">
            <p className="text-2xl font-display font-bold text-foreground">{survey.questions?.length || 0}</p>
            <p className="text-xs text-muted-foreground">Questions</p>
          </CardContent>
        </Card>
      </div>

      {/* Lien public */}
      {survey.public_link && (
        <Card className="shadow-card border-0">
          <CardContent className="p-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 min-w-0">
                <Link className="h-4 w-4 text-primary shrink-0" />
                <span className="text-sm text-muted-foreground truncate">{survey.public_link}</span>
              </div>
              <Button variant="outline" size="sm" className="gap-1 shrink-0"
                onClick={() => { navigator.clipboard.writeText(survey.public_link); toast.success("Lien copié !"); }}>
                <Copy className="h-3.5 w-3.5" />Copier
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Questions */}
        <Card className="shadow-card border-0">
          <CardHeader>
            <CardTitle className="font-display text-lg">Questions ({survey.questions?.length || 0})</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {survey.questions && survey.questions.length > 0 ? (
              survey.questions.map((q, i) => (
                <div key={q.id} className="p-3 rounded-lg bg-muted/50">
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-primary">{i + 1}.</span>
                    <div>
                      <p className="text-sm font-medium text-foreground">
                        {q.text}
                        {q.is_required && <span className="text-destructive ml-1">*</span>}
                      </p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">
                          {q.question_type === "SINGLE_CHOICE"   ? "QCM"
                           : q.question_type === "MULTIPLE_CHOICE" ? "Choix multiples"
                           : q.question_type === "SCALE"           ? "Échelle"
                           : q.question_type === "YES_NO"          ? "Oui/Non"
                           : "Texte libre"}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground">Aucune question</p>
            )}
          </CardContent>
        </Card>

        {/* Informations */}
        <Card className="shadow-card border-0">
          <CardHeader>
            <CardTitle className="font-display text-lg">Informations</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>Début : {survey.start_date}</span>
            </div>
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Calendar className="h-4 w-4" />
              <span>Fin : {survey.end_date}</span>
            </div>
            <div className="text-sm text-muted-foreground">
              Créé par : {survey.creator_username}
            </div>
          </CardContent>
        </Card>
      </div>

      <ConfirmModal
        isOpen={confirmModal.isOpen}
        onClose={() => setConfirmModal({ isOpen: false, type: null })}
        onConfirm={handleConfirmAction}
        title={confirmModal.type === 'archive' ? 'Archiver le sondage' : 'Supprimer le sondage'}
        description={
          confirmModal.type === 'archive'
            ? `Êtes-vous sûr de vouloir ${archived ? 'désarchiver' : 'archiver'} "${survey?.title}" ?`
            : `Êtes-vous sûr de vouloir supprimer "${survey?.title}" ? Cette action est irréversible.`
        }
        confirmText={confirmModal.type === 'archive' ? (archived ? 'Désarchiver' : 'Archiver') : 'Supprimer'}
        variant={confirmModal.type === 'delete' ? 'danger' : 'warning'}
        loading={actionLoading}
      />
    </div>
  );
}

export default SurveyDetail;




































// import { useState, useEffect } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from "../ui";
// import { Archive, ArchiveRestore, ArrowLeft, BarChart3, Calendar, Copy, Edit, Link, Trash2, Users } from "lucide-react";
// import { toast } from "react-toastify";
// import { fetchSurveyById, updateSurvey, deleteSurvey, fetchSurveyResults } from "../../api/survey.api";
// import logger from "../../utils/logger";
// import SurveyDetailSkeleton from "./SurveyDetailSkeleton";
// import storage from "../../utils/storage";
// import { ConfirmModal } from "../common";

// const statusConfig = {
//   Actif: { label: "Actif", className: "bg-success/10 text-success border-success/20" },
//   draft: { label: "Brouillon", className: "bg-muted text-muted-foreground border-muted" },
//   Clôturé: { label: "Clôturé", className: "bg-accent/10 text-accent border-accent/20" },
// };

// function SurveyDetail() {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const [survey, setSurvey] = useState(null);
//   const [results, setResults] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);
//   const [archived, setArchived] = useState(false);
//   const [confirmModal, setConfirmModal] = useState({ isOpen: false, type: null });
//   const [actionLoading, setActionLoading] = useState(false);
//   const role = storage.getRole();
 
  

//   // Charger les données du sondage
//   useEffect(() => {
//     const loadSurvey = async () => {
//       try {
//         setLoading(true);
//         const surveyData = await fetchSurveyById(id);
//         setSurvey(surveyData);
//         setArchived(surveyData.archived || false);

//         // Charger les résultats
//         try {
//           const resultsData = await fetchSurveyResults(id);
//           setResults(resultsData);
//         } catch (err) {
//           logger.warn('Impossible de charger les résultats:', err);
//         }
//       } catch (err) {
//         logger.error('Erreur lors du chargement du sondage:', err);
//         setError('Impossible de charger le sondage');
//         toast.error('Erreur lors du chargement du sondage');
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (id) {
//       loadSurvey();
//     }
//   }, [id]);

//   const handleArchive = async () => {
//     setConfirmModal({
//       isOpen: true,
//       type: 'archive',
//     });
//   };

//   const handleDelete = async () => {
//     setConfirmModal({
//       isOpen: true,
//       type: 'delete',
//     });
//   };

//   const handleConfirmAction = async () => {
//     const { type } = confirmModal;
//     setActionLoading(true);

//     try {
//       if (type === 'archive') {
//         await updateSurvey(id, { archived: !archived });
//         setArchived(!archived);
//         toast.success(archived ? 'Sondage désarchivé avec succès !' : 'Sondage archivé avec succès !');
//       } else if (type === 'delete') {
//         await deleteSurvey(id);
//         toast.success('Sondage supprimé avec succès !');
//         navigate('/gerant/gestion-sondages');
//       }
//       setConfirmModal({ isOpen: false, type: null });
//     } catch (err) {
//       logger.error('Erreur lors de l\'action:', err);
//       const errorMsg = err.response?.data?.error || err.response?.data?.detail || 'Erreur lors de l\'action';
//       toast.error(errorMsg);
//     } finally {
//       setActionLoading(false);
//     }
//   };

//   if (loading) {
//     return <SurveyDetailSkeleton />;
//   }

//   if (error || !survey) {
//     return (
//       <div className="space-y-6">
//         <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
//           <ArrowLeft className="h-4 w-4" />Retour
//         </Button>
//         <div className="bg-red-50 border border-red-200 rounded-lg p-4">
//           <p className="text-red-700">{error || 'Sondage non trouvé'}</p>
//         </div>
//       </div>
//     );
//   }

// //   const progress = survey.total > 0 ? Math.round((survey.responses_count / survey.total) * 100) : 0;
//   const status = statusConfig[survey.computed_status] || survey.is_active === "true" ? statusConfig.Actif : statusConfig.Clôturé;
//   const respondedCount = results?.data?.stats?.responded || 0;
//   const totalParticipants = results?.data?.stats?.total_participants || 0;
//   const progress = results?.data?.stats?.participation_rate || 0;

//   return (
//     <div className="space-y-6">
//       <Button variant="ghost" className="gap-2" onClick={() => navigate(-1)}>
//         <ArrowLeft className="h-4 w-4" />Retour
//       </Button>

      
//         <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
//         <div className="space-y-2">
//           <div className="flex items-center gap-3 flex-wrap">
//             <h1 className="font-display text-2xl font-bold text-foreground">{survey.title}</h1>
//             <Badge variant="outline" className={status.className}>{status.label}</Badge>
//             <Badge variant="outline" className="text-xs">
//               {survey.category === "EVENT" ? "événementiel" : survey.category === "INTERNAL" ? "interne" : survey.category === "EXTERNAL" ? "externe" : survey.category}
//             </Badge>
//             {archived && <Badge variant="outline" className="bg-muted text-xs">Archivé</Badge>}
//             {survey.is_anonymous && <Badge variant="outline" className="text-xs bg-primary/10 text-primary border-primary/20">Anonyme</Badge>}
//           </div>
//           <p className="text-sm text-muted-foreground">{survey.description}</p>
//         </div>
//         {role === 'Gerant' && (
//         <div className="flex gap-2 shrink-0 flex-wrap">
//           <Button variant="outline" size="sm" className="gap-1" onClick={() => navigate(`/gerant/gestion-sondages/modifier/${id}`)}>
//             <Edit className="h-3.5 w-3.5" />Modifier
//           </Button>
//           <Button variant="outline" size="sm" className="gap-1" onClick={handleArchive}>
//             {archived ? <ArchiveRestore className="h-3.5 w-3.5" /> : <Archive className="h-3.5 w-3.5" />}
//             {archived ? "Désarchiver" : "Archiver"}
//           </Button>
//           <Button variant="outline" size="sm" className="gap-1 text-destructive" onClick={handleDelete}>
//             <Trash2 className="h-3.5 w-3.5" />Supprimer
//           </Button>
//         </div>
//       )}
//       </div>

      

//       {/* Quick access cards */}
//       <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//         <Card 
//         className="shadow-card border-0 cursor-pointer hover:shadow-lg transition-shadow group"
//         // onClick={() => navigate(role === 'ADMIN' ?  `/admin/gestion-sondage/${survey.id}/resultats` : `/gerant/gestion-sondages/${survey.id}/resultats`)}
//         onClick={() => toast.info("Accès aux résultats bientôt disponible")}
//         >
//           <CardContent className="p-5 flex items-center gap-4">
//             <div className="h-12 w-12 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
//               <BarChart3 className="h-6 w-6 text-primary" />
//             </div>
//             <div className="flex-1">
//               <p className="font-display font-semibold text-foreground">Voir les résultats</p>
//               <p className="text-xs text-muted-foreground">{respondedCount} réponses · Graphiques et export CSV</p>
//             </div>
//           </CardContent>
//         </Card>
//         <Card
//          className="shadow-card border-0 cursor-pointer hover:shadow-lg transition-shadow group"
//          onClick={() => navigate(`/gerant/gestion-sondages/${id}/participants`)}>
//           <CardContent className="p-5 flex items-center gap-4">
//             <div className="h-12 w-12 rounded-xl bg-accent/10 flex items-center justify-center group-hover:bg-accent/20 transition-colors">
//               <Users className="h-6 w-6 text-accent" />
//             </div>
//             <div className="flex-1">
//               <p className="font-display font-semibold text-foreground">Gérer les participants</p>
//               <p className="text-xs text-muted-foreground">{respondedCount}/{results?.data?.participants?.length } ont répondu · Inviter et relancer</p>
//             </div>
//           </CardContent>
//         </Card>
//       </div>

//       {/* Stats */}
//       <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
//         <Card className="shadow-card border-0">
//           <CardContent className="p-4 text-center">
//             <p className="text-2xl font-display font-bold text-foreground">{respondedCount}</p>
//             <p className="text-xs text-muted-foreground">Réponses</p>
//           </CardContent>
//         </Card>
//         <Card className="shadow-card border-0">
//           <CardContent className="p-4 text-center">
//             <p className="text-2xl font-display font-bold text-primary">{progress}%</p>
//             <p className="text-xs text-muted-foreground">Complétion</p>
//           </CardContent>
//         </Card>
//         <Card className="shadow-card border-0">
//           <CardContent className="p-4 text-center">
//             <p className="text-2xl font-display font-bold text-foreground">{totalParticipants}</p>
//             <p className="text-xs text-muted-foreground">Participants</p>
//           </CardContent>
//         </Card>
//         <Card className="shadow-card border-0">
//           <CardContent className="p-4 text-center">
//             <p className="text-2xl font-display font-bold text-foreground">{survey.questions?.length || 0}</p>
//             <p className="text-xs text-muted-foreground">Questions</p>
//           </CardContent>
//         </Card>
//       </div>

//       {/* Public link */}
//       {survey.public_link && (
//         <Card className="shadow-card border-0">
//           <CardContent className="p-4">
//             <div className="flex items-center justify-between gap-3">
//               <div className="flex items-center gap-2 min-w-0">
//                 <Link className="h-4 w-4 text-primary shrink-0" />
//                 <span className="text-sm text-muted-foreground truncate">{survey.public_link}</span>
//               </div>
//               <Button variant="outline" size="sm" className="gap-1 shrink-0" onClick={() => { navigator.clipboard.writeText(survey.public_link); toast.success("Lien copié !"); }}>
//                 <Copy className="h-3.5 w-3.5" />Copier
//               </Button>
//             </div>
//           </CardContent>
//         </Card>
//       )}

//       <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
//         {/* Questions */}
//         <Card className="shadow-card border-0">
//           <CardHeader>
//             <CardTitle className="font-display text-lg">Questions ({survey.questions?.length || 0})</CardTitle>
//           </CardHeader>
//           <CardContent className="space-y-3">
//             {survey.questions && survey.questions.length > 0 ? (
//               survey.questions.map((q, i) => (
//                 <div key={q.id} className="p-3 rounded-lg bg-muted/50">
//                   <div className="flex items-start gap-2">
//                     <span className="text-xs font-semibold text-primary">{i + 1}.</span>
//                     <div>
//                       <p className="text-sm font-medium text-foreground">
//                         {q.text}
//                         {q.is_required && <span className="text-destructive ml-1">*</span>}
//                       </p>
//                       <div className="flex items-center gap-2 mt-1">
//                         <Badge variant="outline" className="text-xs">
//                           {q.question_type === "SINGLE_CHOICE" ? "QCM" : q.question_type === "MULTIPLE_CHOICE" ? "Choix multiples" : q.question_type === "SCALE" ? "Échelle" : "Texte libre"}
//                         </Badge>
//                       </div>
//                     </div>
//                   </div>
//                 </div>
//               ))
//             ) : (
//               <p className="text-sm text-muted-foreground">Aucune question</p>
//             )}
//           </CardContent>
//         </Card>

//         {/* Dates */}
//         <Card className="shadow-card border-0">
//           <CardHeader>
//             <CardTitle className="font-display text-lg">Informations</CardTitle>
//           </CardHeader>
//           <CardContent className="space-y-3">
//             <div className="flex items-center gap-2 text-sm text-muted-foreground">
//               <Calendar className="h-4 w-4" />
//               <span>Début : {survey.start_date}</span>
//             </div>
//             <div className="flex items-center gap-2 text-sm text-muted-foreground">
//               <Calendar className="h-4 w-4" />
//               <span>Fin : {survey.end_date}</span>
//             </div>
//             <div className="text-sm text-muted-foreground">
//               <span>Créé par : {survey.creator_username}</span>
//             </div>
//           </CardContent>
//         </Card>
//       </div>

//       <ConfirmModal
//         isOpen={confirmModal.isOpen}
//         onClose={() => setConfirmModal({ isOpen: false, type: null })}
//         onConfirm={handleConfirmAction}
//         title={confirmModal.type === 'archive' ? 'Archiver le sondage' : 'Supprimer le sondage'}
//         description={
//           confirmModal.type === 'archive'
//             ? `Êtes-vous sûr de vouloir ${archived ? 'désarchiver' : 'archiver'} "${survey?.title}" ?`
//             : `Êtes-vous sûr de vouloir supprimer "${survey?.title}" ? Cette action est irréversible.`
//         }
//         confirmText={confirmModal.type === 'archive' ? (archived ? 'Désarchiver' : 'Archiver') : 'Supprimer'}
//         variant={confirmModal.type === 'delete' ? 'danger' : 'warning'}
//         loading={actionLoading}
//       />
//     </div>
//   );
// }

// export default SurveyDetail;
